import React from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import type { PublishPreview } from '../../../types/heats';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: PublishPreview | null;
  loading: boolean;
  onConfirm: () => void;
}

/**
 * Récapitulatif AVANT envoi des emails de publication.
 *
 * Cette étape existe pour qu'aucun email ne parte en silence : le décompte des
 * destinataires est calculé côté serveur (`publish/preview`) et montré ici,
 * l'envoi n'ayant lieu qu'au clic de confirmation (garde-fou 2026-07-03).
 */
const ConfirmPublishDialog: React.FC<Props> = ({ open, onOpenChange, preview, loading, onConfirm }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('heats.publishConfirm.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <p className="text-sm text-muted-foreground mb-3">
            {t('heats.publishConfirm.intro', {
              heats: preview?.heats_count ?? 0,
              teams: preview?.teams_count ?? 0,
              recipients: preview?.recipients_total ?? 0,
              // Les emails et les destinataires sont deux nombres différents :
              // quelqu'un inscrit dans deux équipes reçoit deux annonces, avec
              // deux horaires et deux dossards. On annonçait le second en
              // promettant le premier.
              emails: preview?.emails_total ?? preview?.recipients_total ?? 0,
            })}
          </p>

          {preview && preview.breakdown.length > 0 && (
            <div className="rounded-md border border-border divide-y divide-border max-h-64 overflow-y-auto">
              {preview.breakdown.map((row) => (
                <div key={row.category} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="font-medium">{row.category}</span>
                  <span className="text-muted-foreground">
                    {t('heats.publishConfirm.categoryLine', {
                      teams: row.teams,
                      // La ligne annonce des « email(s) » : c'est bien le
                      // nombre d'envois qu'elle doit porter.
                      recipients: row.emails ?? row.recipients,
                    })}
                  </span>
                </div>
              ))}
            </div>
          )}

          {preview && preview.without_account > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              {t('heats.publishConfirm.withoutAccount', { count: preview.without_account })}
            </p>
          )}

          {preview && preview.recipients_total === 0 && (
            <p className="text-sm text-warning mt-2">{t('heats.publishConfirm.noRecipients')}</p>
          )}

          {preview && preview.recipients_total > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              {t('heats.publishConfirm.background')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={loading}>
            {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : <Send size={18} className="mr-2" />}
            {t('heats.publishConfirm.confirm', {
              count: preview?.emails_total ?? preview?.recipients_total ?? 0,
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmPublishDialog;
